import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VendorLocationFormField } from 'contacts/elements';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { useSaveVendorQuoteMainInfo } from 'quotes/hooks';
import { IVendorQuote, IVendorQuoteMainInfoForm } from 'quotes/types';

import { getDefaultValues, mapVendorQuoteMainInfoForm } from './mappers';
import { vendorQuoteMainInfoSchema } from './schema';

interface IProps {
  quote: IVendorQuote;
  isOpen: boolean;
  onClose: () => void;
  showSupplier?: boolean;
}

export const UpdateVendorQuoteMainInfo = ({
  quote,
  isOpen,
  onClose,
  showSupplier = false,
}: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { saveVendorQuoteMainInfo, isSaving } = useSaveVendorQuoteMainInfo();

  const defaultValues = useMemo(() => getDefaultValues(quote), [quote]);
  const isQuoteNumberRequired = !!defaultValues.externalNumber.trim();

  const onSubmit =
    (reset: UseFormReset<IVendorQuoteMainInfoForm>) => async (data: IVendorQuoteMainInfoForm) => {
      try {
        const { success } = await saveVendorQuoteMainInfo(
          mapVendorQuoteMainInfoForm(quote.id, defaultValues, data)
        );

        if (!success) {
          return;
        }
      } catch {
        return;
      }

      setTimeout(() => handleOnClose(reset), 1000);
    };

  const handleOnClose = (reset: UseFormReset<IVendorQuoteMainInfoForm>) => {
    clearNotification();
    onClose();
    // No args: by the delayed close, the refetch has already swapped in the saved values.
    reset();
  };

  return (
    <FormProvider
      schema={vendorQuoteMainInfoSchema(isQuoteNumberRequired)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        title={t('titleEditVendorQuoteDetails')}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelSave')}
        cancelBtnLabel={t('buttonLabelCancel')}
        disabledSubmit={isSaving}
      >
        {showSupplier && (
          // Clearing would read as "remove supplier", but an empty picker means "unchanged".
          <VendorLocationFormField
            name="vendorLocation"
            label={t('labelVendor')}
            disableClearable
          />
        )}

        <FormField
          name="externalNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelVendorQuoteNumber'), required: isQuoteNumberRequired }}
        />

        <FormField
          name="revisionNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelQuoteRevision') }}
        />
      </Form>
    </FormProvider>
  );
};
